import React from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Modal from "../../../components/ui-components/Modal";
import FormButton from "../../../components/form-components/FormButton";
import { toast } from "../../../components/ui-components/Toast";
import { getSkuBreakdown } from "../api";

// Two-decimal currency — finance sometimes needs sub-cent precision when verifying small
// allocations against a partner's invoice. Defaults to "€0.00" when value is missing.
const euro = (n) =>
  `€${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// CSV serialiser — finance routinely pastes audit data into a spreadsheet. Conservative
// quoting: every field is wrapped in quotes with internal quotes doubled, so commas, line
// breaks, and quoted strings in SKU descriptions survive a round-trip into Excel intact.
const escapeCSV = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const buildCsv = (rows, totalMatched) => {
  const header = ["Service", "SKU", "Rows", "Cost"].map(escapeCSV).join(",");
  const body = rows.map((r) =>
    [r.serviceName, r.skuDescription, r.rowCount, (r.totalCost || 0).toFixed(2)]
      .map(escapeCSV)
      .join(",")
  );
  const sampleTotal = rows.reduce((s, r) => s + (r.totalCost || 0), 0);
  const footer = ["", "", "Top 10 total", sampleTotal.toFixed(2)].map(escapeCSV).join(",");
  const headline = totalMatched != null
    ? ["", "", "Headline matched cost", (totalMatched || 0).toFixed(2)].map(escapeCSV).join(",")
    : null;
  return [header, ...body, footer, headline].filter(Boolean).join("\n");
};

// Audit drill-down: groups the matched cost by (service, SKU) so finance verifies the
// *composition* of a source, not just the largest single rows. Answers the actual question
// finance is asking when they open this view: "does the spend mix look right?".
const SkuBreakdownModal = ({ open, onClose, criteria, sourceTypeId, totalMatched, valueLabels }) => {
  const { data, isLoading, error } = useQuery(
    ["sku-breakdown", JSON.stringify(criteria), sourceTypeId],
    () => getSkuBreakdown(criteria, sourceTypeId),
    { enabled: open, keepPreviousData: false },
  );
  const rows = data?.rows || [];
  const sampleTotal = rows.reduce((s, r) => s + (r.totalCost || 0), 0);
  const headline = totalMatched || 0;
  const coverage = headline > 0 ? Math.round((sampleTotal / headline) * 100) : 0;
  // Only show the "Headline matched cost" row when there's a gap to communicate. When the
  // top 10 already covers the entire matched cost (typical for tight sources with ≤ 10 SKUs)
  // the two rows would just repeat the same number — pure noise.
  const showHeadlineRow = headline > 0 && coverage < 100;

  const copyCsv = async () => {
    try {
      await navigator.clipboard.writeText(buildCsv(rows, totalMatched));
      toast.success("Copied breakdown to clipboard");
    } catch {
      toast.error("Couldn't copy — your browser blocked clipboard access");
    }
  };

  // Project breakdown is redundant when the source is scoped to a single project — every
  // row would show the same name. The valueLabels prop is still wired so the column can
  // be added back later if we extend the breakdown to per-project.
  void valueLabels;

  // Promoting Copy as CSV into the Modal's confirmButton slot puts both action affordances
  // on the same bar with the same FormButton styling — fixes the "underline link vs proper
  // button at different levels" disconnect. Only offer the action when there are rows.
  const hasRows = !isLoading && !error && rows.length > 0;

  return (
    <Modal
      show={open}
      close={onClose}
      title="Audit by SKU"
      description={<span>Top 10 SKU groups over the last 30 days.</span>}
      // Suppress Modal's default action bar (which uses pl-32 anchored to the icon column)
      // and render our own action row inside `children` below — that keeps the buttons
      // left-aligned with the table instead of floating ~60px to the left of it.
      cancelButton=""
      size="lg"
      // circle-info reads as "informational" instead of triangle-exclamation's "something
      // is wrong" — this is a benign drill-down, not an alert. alertIcon stays unset, so
      // the surrounding background is the calm lightblue (Modal's default for non-alerts).
      icon={icon({ name: "circle-info", style: "solid" })}
    >
      {/* pr-2 gives the SHARE column a little breathing room so its right-aligned percentages
          don't visually crowd the modal's right edge. */}
      <div className="mt-2 pr-2">
        {isLoading && (
          <p className="text-sm text-text-2 py-6 text-center">Loading breakdown…</p>
        )}
        {error && (
          <div className="py-6 text-center">
            <p className="text-sm text-danger">Couldn't load the breakdown.</p>
            {(error?.message || error?.status) && (
              <p className="mt-1 text-[11px] text-text-2 font-mono">
                {error.status ? `${error.status} ` : ""}{error.message || ""}
              </p>
            )}
          </div>
        )}
        {!isLoading && !error && rows.length === 0 && (
          <p className="text-sm text-text-2 py-6 text-center italic">
            No matching cost in the last 30 days.
          </p>
        )}
        {hasRows && (
          <table className="w-full text-xs text-text">
            <thead className="text-[11px] uppercase tracking-wide text-text-2">
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-4 font-medium">SKU</th>
                <th className="text-left py-2 pr-4 font-medium">Service</th>
                <th className="text-right py-2 pr-4 font-medium">Rows</th>
                <th className="text-right py-2 pr-4 font-medium">Cost</th>
                <th className="text-right py-2 font-medium w-12">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const share = headline > 0 ? Math.round((row.totalCost / headline) * 100) : 0;
                return (
                  <tr key={idx} className="border-b border-border last:border-b-0 align-top">
                    {/* SKU full text — let it wrap. Truncating finance's audit signal defeats
                        the purpose of the drill-down. */}
                    <td className="py-2 pr-4 text-text break-words">{row.skuDescription || "—"}</td>
                    <td className="py-2 pr-4 whitespace-nowrap text-text-2">{row.serviceName || "—"}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-text-2 whitespace-nowrap">× {row.rowCount}</td>
                    <td className="py-2 pr-4 text-right font-medium tabular-nums whitespace-nowrap">{euro(row.totalCost)}</td>
                    <td className="py-2 text-right tabular-nums text-text-2">{share}%</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border">
                <td className="pt-3 pb-2 pr-4 font-semibold text-text text-xs" colSpan={3}>
                  {showHeadlineRow ? "Top 10 total" : "Total"}
                </td>
                <td className="pt-3 pb-2 pr-4 text-right font-semibold tabular-nums text-text whitespace-nowrap">{euro(sampleTotal)}</td>
                <td className="pt-3 pb-2 text-right tabular-nums font-semibold text-text">{coverage}%</td>
              </tr>
              {showHeadlineRow && (
                <tr>
                  <td className="py-1 pr-4 text-[11px] text-text-2" colSpan={3}>Headline matched cost</td>
                  <td className="py-1 pr-4 text-right text-[11px] text-text-2 tabular-nums whitespace-nowrap">{euro(headline)}</td>
                  <td className="py-1 text-right text-[11px] text-text-2">100%</td>
                </tr>
              )}
            </tfoot>
          </table>
        )}
      </div>

      {/* Action row inside the content column → left-aligns with the table. Copy as CSV is
          only offered when there are rows; Close is always available. */}
      <div className="mt-6 flex items-center gap-2">
        {hasRows && <FormButton title="Copy as CSV" style="primary" onClick={copyCsv} />}
        <FormButton title="Close" style="secondary" onClick={onClose} />
      </div>
    </Modal>
  );
};

SkuBreakdownModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  criteria: PropTypes.object.isRequired,
  sourceTypeId: PropTypes.string,
  // Headline matched cost from the live preview — used to render the % share column and
  // the "top-10 covers X% of the headline" footer line when there's a gap to show.
  totalMatched: PropTypes.number,
  // Reserved for a future per-project column when scope spans multiple projects.
  valueLabels: PropTypes.object,
};

export default SkuBreakdownModal;
